import React, { useState, useEffect, FunctionComponent, Fragment } from "react"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { groupBy, convertLevels, convertLanguage, convertTrainings } from "@murphy-frontend/common/utils"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import CourseCarousel, { CourseCarouselViewModel } from "../../common/components/TrainingComponents/CourseCarousel"
import SearchBar from "@murphy-frontend/web-core/components/SearchBar"
import { CustomerUserTrainingSessionStates, TrainingTypes } from "../../../../common/enums"
import { courseLanguages, courseLevels, courseTrainings, customerUserTrainingSessionStates } from "@murphy-frontend/web-core/constants"
import { translateConstants } from "../../common/services/translationService"
import { useGetTrainingSessions } from "../../common/api/TrainingSessions/queries"
import { useGetTrainings } from "../../common/api/Training/queries"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext"
import { TimeZoneType, getConvertedDate } from "@murphy-frontend/common/services/TimeService"
import useWindowDimensions from "@murphy-frontend/web-core/hooks/useWindowDimensions"
import { useCustomer } from "../../common/contexts/CustomerContext"
import { useUser } from "../../common/contexts/UserContext"
import { TrainingModel } from "../../common/api/Training/TrainingApi"
import { TrainingSessionModel } from "../../common/api/TrainingSessions/TrainingSessionsApi"
import { Constant } from "../../common/commonInterfaces"
import { FilterType } from "@murphy-frontend/web-core/components/FilterControl"
import { UserTrainingSession } from "../../common/api/UserTrainingSessions/IUserTrainingSessionsApi"
import { useGetUserTrainingSessionsByCustomerUserId } from "../../common/api/UserTrainingSessions/queries"
import CourseRegistrationOnboardingSteps from "./CourseRegistrationOnboardingSteps"
import { OnboardingFlowIds, useOnboarding } from "../../common/contexts/OnboardingContext"
import { parseHtml } from "@murphy-frontend/web-core/features/RichText/helpers"
import RichTextDisplay from "@murphy-frontend/web-core/features/RichText/RichTextDisplay"
import InfoIcon from "@murphy-frontend/web-core/components/InfoIcon"
import { IconContext } from "react-icons"
import { IoChevronDown, IoChevronUp } from "react-icons/io5"
import Button from "@murphy-frontend/web-core/components/Button"
import FilterModal from "./components/FilterModal"
import FilterToggle from "@murphy-frontend/web-core/components/FilterToggle"
import ComponentWrapper from "../../common/components/ComponentWrapper"
import { useGetUser } from "@murphy-frontend/common/api/Users/Queries"

const CoursePortal: FunctionComponent = () => {
	const {
		openModal,
		closeModal
	} = useModal()

	const {
		translations,
		timeZone,
		language,
		selectedCourseLanguages,
		selectedCourseLevels,
		selectedCourseTrainings,
		setSelectedCourseLanguages,
		setSelectedCourseLevels,
		setSelectedCourseTrainings
	} = useUserPreferences()

	const { currentSize } = useWindowDimensions()

	const navigate = useNavigate()
	const { customer } = useCustomer()
	const { user } = useUser()
	const { isLoading: isLoadingUser, isError: isErrorUser, data: dataUser, error: errorUser } = useGetUser(user.CustomerUserId)

	const [error, setError] = useState(null)

	const [groupedTrainingSessions, setGroupedTrainingSessions] = useState<GroupedTrainingSession[]>([])
	const [trainings, setTrainings] = useState<TrainingModel[]>([])
	const [customerUserTrainingSessions, setCustomerUserTrainingSessions] = useState<UserTrainingSession[]>([])
	const [courseViews, setCourseViews] = useState<CourseCarouselViewModel[]>([])
	const [userSessionStates, setUserSessionStates] = useState<Constant[]>([])

	const [filters, setFilters] = useState<FilterType[]>([])
	const [filterCount, setFilterCount] = useState(0)
	const [markAllIsToggled, setMarkAllIsToggled] = useState<boolean>(true)
	const [showFilter, setShowFilter] = useState(false)

	const [filteredCourseViews, setFilteredCourseViews] = useState([])
	const [currentSearchText, setCurrentSearchText] = useState("")

	const [stepsEnabled, setStepsEnabled] = useState<boolean>(false)
	const [onboardingTerminated, setOnboardingTerminated] = useState<boolean>(null)

	const {
		setOnboardingIdIsComplete,
		incompletedOnboardingFlows,
		isLoading: onBoardingIsLoading,
		resetOnboarding,
		setCurrentStep,
		onboardingsCurrentStep
	} = useOnboarding()

	const {
		isLoading: trainingSessionsIsLoading,
		isError: trainingSessionsIsError,
		data: trainingSessionsData,
		error: trainingSessionsError
	} = useGetTrainingSessions({ customerId: customer.Id })

	const {
		isLoading: trainingsIsLoading,
		isError: trainingsIsError,
		data: trainingsData,
		error: trainingsError
	} = useGetTrainings({ customerUserId: user.CustomerUserId })

	const {
		isLoading: customerUserTrainingSessionsIsLoading,
		isError: customerUserTrainingSessionsIsError,
		data: customerUserTrainingSessionsData,
		error: customerUserTrainingSessionsError
	} = useGetUserTrainingSessionsByCustomerUserId(user.CustomerUserId)

	interface GroupedTrainingSession {
		Id: number
		Name: string
		Description: string
		trainingSessions: TrainingSessionModel[]
	}

	const onToggleMarkAllFilters = () => {
		const newAllRows = filters.map(p => {
			return { ...p, isActive: !markAllIsToggled }
		})

		setFilters(newAllRows)
		setMarkAllIsToggled(!markAllIsToggled)
		syncSelectedCourseLanguages(newAllRows)
	}

	const onToggleFilter = (filt: FilterType) => {
		const updatedFilters = filters.map(filter => {
			const updatedItem = { ...filter }

			if (filter.columnname === filt.columnname && filter.filtervalue === filt.filtervalue) {
				updatedItem.isActive = !filter.isActive
			}

			return updatedItem
		})

		if (updatedFilters.every(p => p.isActive)) {
			setMarkAllIsToggled(true)
		} else {
			setMarkAllIsToggled(false)
		}

		setFilters(updatedFilters)
		syncSelectedCourseLanguages(updatedFilters)
	}

	const onUpdateFilters = (updatedFilters: FilterType[]) => {
		if (updatedFilters.every(p => p.isActive)) {
			setMarkAllIsToggled(true)
		} else {
			setMarkAllIsToggled(false)
		}

		setFilters(updatedFilters)
		syncSelectedCourseLanguages(updatedFilters)
	}

	const onResetFilters = () => {
		const initFilters = generateInitialFilters()
		setFilters(initFilters)
		syncSelectedCourseLanguages(initFilters)
	}

	const generateInitialFilters = () => {
		const mappedLanguages = courseLanguages.map(p => {
			return {
				displayName: p.value,
				columnname: "language",
				filtervalue: p.id,
				isActive: selectedCourseLanguages.includes(p.id)
			}
		})

		const mappedLevels = courseLevels.map(p => {
			return {
				displayName: convertLevels(p.id, translations),
				columnname: "level",
				filtervalue: p.id,
				isActive: selectedCourseLevels.includes(p.id)
			}
		})

		const mappedTrainings = courseTrainings.map(p => {
			return {
				displayName: convertTrainings(p.id, translations),
				columnname: "live",
				filtervalue: p.id,
				isActive: selectedCourseTrainings.includes(p.id)
			}
		})

		const allPossibleFilters = [...mappedLanguages, ...mappedLevels, ...mappedTrainings]

		return allPossibleFilters
	}

	const resetFilters = () => {
		const allFilters = [...filters].map(filter => {
			return { ...filter, isActive: false }
		})

		setFilters(allFilters)
		setCurrentSearchText("")
		syncSelectedCourseLanguages(allFilters)
	}

	const syncSelectedCourseLanguages = (filters: FilterType[]) => {
		const languageFilters = filters
			.filter(p => p.columnname === "language" && p.isActive === true)
			.map(p => p.filtervalue)
		setSelectedCourseLanguages(languageFilters)

		const levelFilters = filters
			.filter(p => p.columnname === "level" && p.isActive === true)
			.map(p => p.filtervalue)
		setSelectedCourseLevels(levelFilters)

		const trainingFilters = filters
			.filter(p => p.columnname === "live" && p.isActive === true)
			.map(p => p.filtervalue)
		setSelectedCourseTrainings(trainingFilters)
	}

	useEffect(() => {
		if (translations) {
			const initFilters = generateInitialFilters()
			setFilters(initFilters)
		}
	}, [translations, selectedCourseLanguages, selectedCourseLevels, selectedCourseTrainings])

	const createCourseViews = (
		trainingsRaw: TrainingModel[],
		groupedTrainingSessionsRaw: GroupedTrainingSession[],
		customerUserTrainingSessionsRaw: UserTrainingSession[],
		states: Constant[],
		tz: TimeZoneType
	): CourseCarouselViewModel[] => {
		const newCourseViews = groupedTrainingSessionsRaw
			.filter(p => trainingsRaw.some(q => q.Id === p.Id))
			.map(p => {
				const matchingCustomerUserTrainingSessions = customerUserTrainingSessionsRaw.filter(q => q.TrainingId === p.Id && q.TrainingSessionStateId !== CustomerUserTrainingSessionStates.Cancelled)

				const matchingTraining = trainingsRaw.find(q => q.Id === p.Id)
				const sortedTrainingSessions = [...p.trainingSessions.sort((a, b) => new Date(a.StartDateTimeUtc).valueOf() - new Date(b.StartDateTimeUtc).valueOf())]

				if (!matchingTraining) {
					return null
				}

				const courseView: CourseCarouselViewModel = {
					id: p.Id,
					trainingTypeId: matchingTraining.TrainingTypeId,
					name: matchingTraining.Name,
					description: matchingTraining.Description,
					trainingSessions: sortedTrainingSessions,
					backGroundUrl: matchingTraining.ImageUrl,
					onClick: () => navigate(`/training/${p.Id}`),
					userTrainingSessions: [],
					language: convertLanguage(matchingTraining.TrainingLanguageId),
					level: matchingTraining.Levels,
					live: matchingTraining.Live,
					freemium: matchingTraining.Freemium,
					isMandatory: matchingTraining.IsMandatory
				}

				if (matchingCustomerUserTrainingSessions.length > 0) {
					courseView.userTrainingSessions = matchingCustomerUserTrainingSessions.map(r => ({
						userTrainingSessionId: r.ID,
						trainingSessionStateId: r.TrainingSessionStateId,
						urlToMeeting: r.UrlToMeeting,
						trainingSessionStateName: states.filter(q => q.id === r.TrainingSessionStateId)[0].value,
						trainingSessionStartDate: getConvertedDate(dayjs.utc(r.StartDateTimeUtc), tz),
						trainingSessionEndDate: getConvertedDate(dayjs.utc(r.EndDateTimeUtc), tz)
					}))
				}

				return courseView
			}).filter(courseView => courseView !== null)

		return newCourseViews
	}

	const groupTrainingSessions = (sessions: TrainingSessionModel[]): GroupedTrainingSession[] => {
		const newGroupedTrainingSessions = groupBy(sessions, "TrainingId")

		const mappedCourses = Object.values(newGroupedTrainingSessions).map(
			(trainingSessions: TrainingSessionModel[]) => ({
				Id: trainingSessions[0].TrainingId,
				Name: trainingSessions[0].Name,
				Description: trainingSessions[0].Desc,
				trainingSessions
			})
		)
		return mappedCourses
	}

	useEffect(() => {
		const translatedUserSessionStates = translateConstants(language, customerUserTrainingSessionStates)
		setUserSessionStates(translatedUserSessionStates)

		return () => {
			setUserSessionStates([])
		}
	}, [language])

	useEffect(() => {
		if (!trainingsIsLoading) {
			setTrainings(trainingsData)
		}
		return () => {
			setTrainings([])
		}
	}, [trainingsData, trainingsIsLoading])

	useEffect(() => {
		if (!trainingSessionsIsLoading) {
			const filteredBookableTrainingSessions = trainingSessionsData.filter(p => dayjs.utc(p.StartDateTimeUtc) > dayjs.utc())
			const mappedTrainingsSessions = groupTrainingSessions(filteredBookableTrainingSessions)
			setGroupedTrainingSessions(mappedTrainingsSessions)
		}
	}, [trainingSessionsData, trainingSessionsIsLoading])

	useEffect(() => {
		if (!customerUserTrainingSessionsIsLoading) {
			setCustomerUserTrainingSessions(customerUserTrainingSessionsData)
		}
	}, [customerUserTrainingSessionsData, customerUserTrainingSessionsIsLoading])

	useEffect(() => {
		if (groupedTrainingSessions && userSessionStates.length > 0 && timeZone && trainings.length > 0) {
			const newCourseViews = createCourseViews(
				trainings,
				groupedTrainingSessions,
				customerUserTrainingSessions,
				userSessionStates,
				timeZone
			)

			const filteredCourseViews = newCourseViews.filter(p => p.trainingTypeId === TrainingTypes.Course)
			setCourseViews(newCourseViews)
		}
	}, [customerUserTrainingSessions, groupedTrainingSessions, userSessionStates, trainings, timeZone])

	const filterRows = (
		filters: FilterType[],
		courseViewsCopy: CourseCarouselViewModel[],
		rowsWithActiveFilters: CourseCarouselViewModel[]
	) => {
		const ids = [...filters].map(el => el.filtervalue)
		const currentFilter = filters[0]

		const currentFilterMatchingRows = courseViewsCopy.filter((recipient: any) => {
			if (ids.includes(recipient[currentFilter.columnname])) {
				return recipient
			}
		})

		rowsWithActiveFilters = rowsWithActiveFilters.concat(currentFilterMatchingRows)

		return rowsWithActiveFilters
	}

	useEffect(() => {
		let courseViewsCopy = [...courseViews]

		if (courseViews) {
			if (currentSearchText) {
				const searchValue = currentSearchText.toUpperCase()
				courseViewsCopy = courseViewsCopy.filter(p => p.name.toUpperCase().startsWith(searchValue))
			}
		}

		if (filters) {
			let rowsWithActiveFilters: CourseCarouselViewModel[] = []
			const langRows = filters.filter(filter => filter.columnname === "language" && filter.isActive)
			const levelRows = filters.filter(filter => filter.columnname === "level" && filter.isActive)
			const trainingRows = filters.filter(filter => filter.columnname === "live" && filter.isActive)

			if (langRows.length && levelRows.length) {
				const langIds = [...langRows].map(el => el.filtervalue)
				const filterLang = langRows[0]
				const levelIds = [...levelRows].map(el => el.filtervalue)
				const filterLevel = levelRows[0]

				const currentFilterMatchingRows = courseViewsCopy.filter((recipient: any) => {
					if (langIds.includes(recipient[filterLang.columnname]) && levelIds.includes(recipient[filterLevel.columnname])) {
						return recipient
					}
				})

				rowsWithActiveFilters = rowsWithActiveFilters.concat(currentFilterMatchingRows)
				courseViewsCopy = rowsWithActiveFilters
			} else if (langRows.length) {
				courseViewsCopy = filterRows(langRows, courseViewsCopy, rowsWithActiveFilters)
			} else if (trainingRows.length) {
				courseViewsCopy = filterRows(trainingRows, courseViewsCopy, rowsWithActiveFilters)
			} else if (levelRows.length) {
				courseViewsCopy = filterRows(levelRows, courseViewsCopy, rowsWithActiveFilters)
			}
		}

		// Sort courses so mandatory ones appear first
		courseViewsCopy.sort((a, b) => {
			// If one is mandatory and the other isn't, put the mandatory one first
			if (a.isMandatory && !b.isMandatory) return -1;
			if (!a.isMandatory && b.isMandatory) return 1;

			// Otherwise maintain original order
			return 0;
		});

		setFilteredCourseViews(courseViewsCopy)
	}, [courseViews, filters, currentSearchText])

	useEffect(() => {
		const count = filters.reduce((acc, curr) => (curr.isActive ? acc + 1 : acc), 0)
		setFilterCount(count)
	}, [filters])

	function ErrorMessage() {
		return <p>{error}</p>
	}

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError(null)
				}
			}

			openModal(modalConf)
		}
	}, [error])

	const showStartCourseModal = () => {
		const modalConf: ModalConfiguration = {
			okCallback: () => { console.log("onclick") },
			onCloseModal: () => { console.log("on close") },
			type: 'tutorial',
			centerContent: false,
			body: <div>
				<div style={{ display: 'flex', flexDirection: 'column', backgroundColor: 'white', width: '35rem' }}>
					<img src={require('../../assets/img/startcourse.png')} style={{ height: '6rem', marginTop: 40, marginLeft: 30, marginRight: 30, marginBottom: 20 }}/>
					<div style={{ paddingLeft: 30, paddingRight: 30, display: 'flex', flexDirection: 'column'}}>
						<label style={{ marginBottom: 10, fontSize: 18, fontWeight: 'bold', color: '#222222' }}>Haven’t started a course yet?</label>
						<label style={{ marginBottom: 10, fontSize: 14, color: '#7F7F7F'}}>
							You’ve got free access to four beginner-friendly trainings — the perfect way to build your crisis readiness from day one.
						</label>
						<label style={{fontSize: 14, color: '#7F7F7F'}}>Start with:</label>
						<label style={{fontSize: 16, fontWeight: 'bold', color: '#00726C'}}>Crisis Management – Level 1</label>
						<div style={{ marginTop: 30, marginBottom: 30, display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
							<label style={{textDecorationLine: 'underline', color: '#7F7F7F', marginRight: 20, cursor: 'pointer'}} onClick={()=>closeModal()}>Close</label>
							<Button buttonStyle='btn--primary' onClick={() => { closeModal(); }}>
								Go to Course
							</Button>
						</div>

					</div>
				</div>
			</div>,
			hideButton: true,
			position: 'center'
		}
		openModal(modalConf);
	}
	useEffect(() => {
		const showCourseModal = sessionStorage.getItem('showCourseModal') === 'false';
		if (!showCourseModal && dataUser && customer.IsFreemium && (!dataUser.UserTrainingSessions || dataUser.UserTrainingSessions?.length === 0)) {
			sessionStorage.setItem('showCourseModal', 'false');
			showStartCourseModal();
		}
	}, [dataUser])

	useEffect(() => {
		if (onboardingTerminated === true) {
			return
		}

		const shouldShowOnboarding = incompletedOnboardingFlows
			.map(p => p.OnboardingFlowId)
			.includes(OnboardingFlowIds.Academy)

		if (shouldShowOnboarding && !onBoardingIsLoading) {
			const currentStep = onboardingsCurrentStep.get(OnboardingFlowIds.Academy)

			if (currentStep === 0) {
				const stepsFromApi = incompletedOnboardingFlows.find(p => p.OnboardingFlowId === OnboardingFlowIds.Academy)?.StepsHtmlContents

				const introText = stepsFromApi.get(1)
				const parsedHtmlIntro = introText ? parseHtml(introText) : ""

				const onClickOk = () => {
					setCurrentStep(OnboardingFlowIds.Academy, 1)
					closeModal()
					setStepsEnabled(true)
				}

				const onClickClose = () => {
					setOnboardingIsComplete()
				}

				const modalConf: ModalConfiguration = {
					okCallback: onClickOk,
					onCloseModal: onClickClose,
					type: "info",
					centerContent: true,
					body: (
						<div className='intro-outro-container'>
							<RichTextDisplay content={parsedHtmlIntro} />
						</div>
					),
					title: "Tutorial",
					buttonText: "Start",
					position: "center"
				}

				openModal(modalConf)
			} else {
				setStepsEnabled(true)
			}
		}
	}, [incompletedOnboardingFlows, onBoardingIsLoading, onboardingsCurrentStep])

	const onClickFilter = () => {
		const modalConf: ModalConfiguration = {
			body: (
				<FilterModal
					allFilters={filters}
					closeModal={closeModal}
					toggleFilter={onUpdateFilters}
					reset={resetFilters}
					translations={translations}
				/>
			),
			buttonText: "Ok",
			hideButton: true
		}

		openModal(modalConf)
	}

	const onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value
		setCurrentSearchText(newValue)
	}

	const handleOnboardingComplete = () => {
		setOnboardingIsComplete()
	}

	const setOnboardingIsComplete = () => {
		setOnboardingTerminated(true)
		setOnboardingIdIsComplete(OnboardingFlowIds.Academy)
		setCurrentStep(OnboardingFlowIds.Academy, 0)
		setStepsEnabled(false)
	}

	const onClickStartOnboarding = () => {
		setOnboardingTerminated(false)
		resetOnboarding(OnboardingFlowIds.Academy)
	}

	const stepsComponent = (
		<CourseRegistrationOnboardingSteps
			stepsEnabled={stepsEnabled}
			onComplete={handleOnboardingComplete}
			setStepsEnabled={setStepsEnabled}
		/>
	)

	const stepsIcon = (
		<InfoIcon
			place={"right-end"}
			onHoverContents={<Button onClick={onClickStartOnboarding}>Start tutorial</Button>}
			clickable={true}
		/>
	)

	const handleShowFilter = () => {
		setShowFilter(!showFilter)
	}

	if (trainingSessionsIsLoading || trainingsIsLoading || customerUserTrainingSessionsIsLoading) {
		return (
			<ComponentWrapper active={4}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	const onCardClick = () => {
		const modalConf: ModalConfiguration = {
			okCallback: () => { console.log("onclick") },
			onCloseModal: () => { console.log("on close") },
			type: 'tutorial',
			centerContent: false,
			body: <div>
				<div style={{ display: 'flex', flexDirection: 'column', backgroundColor: 'white', width: '30rem' }}>
					<img src={require('../../assets/img/academylock.png')} style={{ height: '10rem', marginTop: 40, marginLeft: 30, marginRight: 30, marginBottom: 20 }}/>
					<img src={require('../../assets/img/premium.png')} style={{position: 'absolute', top: '50px', left: '40px', width: '100px'}}/>
					<div style={{ paddingLeft: 30, paddingRight: 30, display: 'flex', flexDirection: 'column'}}>
						<label style={{ marginBottom: 10, fontSize: 18, fontWeight: 'bold', color: '#222222' }}>This course is part of Premium</label>
						<label style={{ marginBottom: 5, fontSize: 14, color: '#7F7F7F'}}>
							Looks like this course isn’t included in your current plan. Upgrade to Premium to unlock 100+ instructor-led trainings — including this one — designed to strengthen your team’s crisis response.
						</label>
						<label style={{fontSize: 14, color: '#7F7F7F'}}>You’ll get access to:</label>
						<ul style={{marginLeft: 20, color: '#7F7F7F', fontSize: 14, listStyleType: 'disc'}}>
							<li>Expert-led live sessions</li>
							<li>Flexible scheduling</li>
							<li>Courses tailored to real-world impact</li>
						</ul>
						<div style={{ marginTop: 30, marginBottom: 30, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center'}}>
							<label style={{textDecorationLine: 'underline', color: '#7F7F7F', marginRight: 20, cursor: 'pointer'}} onClick={()=>closeModal()}>Book a Demo</label>
							<Button buttonStyle='btn--primary' onClick={() => {
											closeModal();
											window.open(
												"https://calendly.com/d/cs5c-gbr-zj7/go-premiun",
												"_blank",
												"noopener,noreferrer"
											);
}}>
								Go Premium
							</Button>
						</div>

					</div>
				</div>
			</div>,
			hideButton: true,
			position: 'center'
		}
		openModal(modalConf);
	}

	return (
		<ComponentWrapper active={4}>
			<div className='generalwrapper'>
				{stepsEnabled && stepsComponent}
				<div className='one header-with-info-button'>
					<h1>{translations.coursesmodulename}</h1>
					{stepsIcon}
				</div>
				{currentSize === "xs" ? (
					<section className='search-bar-container--mobile'>
						<SearchBar
							val={currentSearchText}
							handleChange={onSearchChanged}
							customClasses={["search-bar-full-width", "search-bar-full-width--xs"]}
							placeholder={translations["placeholder-lang-search-course"]}
							showSearchIcon={true}
						/>
						<div className='filter-wrapper'>
							<div className='filter-count-container' onClick={handleShowFilter}>
								<div>{`${translations["lang-filter"]} (${filterCount})`}</div>
								<IconContext.Provider value={{ className: "btn-icon" }}>
									{showFilter ? <IoChevronUp /> : <IoChevronDown />}
								</IconContext.Provider>
							</div>
							<div className={showFilter ? "filter-modal-xs-container" : "filter-modal-xs-container--hide"}							>
								<FilterModal
									allFilters={filters}
									closeModal={closeModal}
									toggleFilter={onUpdateFilters}
									reset={resetFilters}
									translations={translations}
								/>
							</div>
						</div>
					</section>
				) : (
					<section className='search-bar-container'>
						<SearchBar
							val={currentSearchText}
							handleChange={onSearchChanged}
							customClasses={["search-bar-full-width"]}
							placeholder={translations["placeholder-lang-search-course"]}
							showSearchIcon={true}
						/>
						<Button isDarkButton={true} onClick={() => onClickFilter()}>
							{translations["lang-filter"]}
						</Button>
					</section>
				)}
				<section className='filters-container'>
					{filters.map(filter => (
						<Fragment key={filter.displayName}>
							{filter.isActive && (
								<FilterToggle
									filterName={filter.displayName}
									key={filter.displayName}
									checked={filter.isActive}
									handleClick={() => onToggleFilter(filter)}
								/>
							)}
						</Fragment>
					))}
				</section>
				<section className='user-table-container'>
					<div className='course-registration-container'>
						<CourseCarousel
							translations={translations}
							courseViews={filteredCourseViews}
							currentScreenSize={currentSize}
							timeZone={timeZone}
							language={language}
							onClick={() => onCardClick()}
						/>
					</div>
				</section>
			</div>
		</ComponentWrapper>
	)
}

export default CoursePortal